import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCodeBranch,
  faServer,
  faDatabase,
  faCloud,
  faCubes,
  faBolt,
  faTerminal,
  faCube,
  faProjectDiagram,
  faShieldAlt,
  faGlobe,
  faToolbox,
  faCogs,
  faSitemap,
  faRobot,
  faMoneyCheckDollar,
  faFileCode,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCode,
  faExchangeAlt,
  faSatelliteDish,
  faNetworkWired,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";

import {
  faReact,
  faNodeJs,
  faLaravel,
  faPython,
  faJs,
  faPhp,
  faHtml5,
  faCss3Alt,
  faGit,
  faGithub,
  faGitlab,
  faDocker,
  faAws,
  faGoogle,
  faMicrosoft,
  faNpm,
} from "@fortawesome/free-brands-svg-icons";
import colorSharp from "../assets/img/color-sharp.png";

export const Experience = () => {
  const [activeTab, setActiveTab] = useState("languages");

  const iconMap = {
  // Languages
  "C/C++": faCodeBranch,
  Python: faPython,
  JavaScript: faJs,
  TypeScript: faCode,
  HTML: faHtml5,
  CSS: faCss3Alt,
  PHP: faPhp,

  // Frameworks / libs
  React: faReact,
  "React Native": faReact,
  Redux: faReact,
  "Redux Toolkit": faReact,
  Nextjs: faReact,
  "Next.js": faReact,
  Vue: faReact,
  Laravel: faLaravel,
  Django: faPython,
  "Express.js": faNodeJs,
  ExpressJS: faNodeJs,
  "PHP Filament": faPhp,
  "Blade (Laravel)": faPhp,
  "Material UI": faCube,
  "Tailwind CSS": faCss3Alt,
  Bootstrap: faCss3Alt,
  jQuery: faFileCode,
  Axios: faExchangeAlt,
  "Socket.io": faBolt,
  GraphQL: faProjectDiagram,
  Apollo: faSatelliteDish,
  Prisma: faDatabase,
  Sequelize: faDatabase,
  TypeORM: faDatabase,
  Zustand: faToolbox,

  // DB & Servers
  MySQL: faDatabase,
  PostgreSQL: faDatabase,
  MongoDB: faDatabase,
  ElasticSearch: faSearch,
  Redis: faDatabase,
  "Firebase Firestore": faDatabase,
  DynamoDB: faDatabase,
  "Apache HTTP Server": faServer,
  Nginx: faServer,

  // DevOps / Cloud
  Docker: faDocker,
  Kubernetes: faCubes,
  "AWS (EC2, S3, RDS, Lambda)": faAws,
  AWS: faAws,
  "Google Cloud Platform (GCP)": faGoogle,
  Azure: faMicrosoft,
  "Azure DevOps": faMicrosoft,
  "CI/CD Pipelines (Jenkins, GitHub Actions)": faCogs,
  Terraform: faCubes,
  Ansible: faCogs,
  Kafka: faBolt,
  RabbitMQ: faBolt,
  PM2: faCogs,
  Linux: faTerminal,
  "Nginx Reverse Proxy": faServer,

  // Tools
  Git: faGit,
  GitHub: faGithub,
  GitLab: faGitlab,
  Bitbucket: faCodeBranch,
  VSCode: faTerminal,
  "IntelliJ IDEA": faTerminal,
  Eclipse: faTerminal,
  Postman: faToolbox,
  Composer: faNpm,
  "Swagger API": faFileCode,
  OpenAPI: faFileCode,
  WebSockets: faBolt,

  // AI / ML
  "OpenAI API": faRobot,
  "LLM Integration (OpenAI)": faRobot,
  TensorFlow: faRobot,
  PyTorch: faRobot,
  "Scikit-Learn": faRobot,
  OCR: faRobot,
  LangChain: faRobot,
  "Vector DBs": faDatabase,

  // Payments
  Razorpay: faMoneyCheckDollar,
  Stripe: faMoneyCheckDollar,
  PayPal: faMoneyCheckDollar,
  "Authorize.net": faMoneyCheckDollar,
  Cashfree: faMoneyCheckDollar,

  // Architecture / Strengths
  Microservices: faSitemap,
  "Monolithic Architecture": faProjectDiagram,
  "RESTful APIs": faNetworkWired,
  "System Design": faSitemap,
  "Scalable Architecture": faProjectDiagram,
  "RBAC & Auth": faUserShield,
  "Advanced Queries": faDatabase,
  Indexing: faDatabase,
  Optimization: faCogs,
};
const skills = {
  languages: [
    "C/C++",
    "Python",
    "JavaScript",
    "TypeScript",
    "HTML",
    "CSS",
    "PHP",
  ],
  frameworks: [
    "Laravel",
    "React",
    "Next.js",
    "Django",
    "Express.js",
    "Tailwind CSS",
    "Bootstrap",
    "React Native",
    "Redux Toolkit",
    "GraphQL",
    "Apollo",
    "Prisma",
    "Sequelize",
    "TypeORM",
    "Zustand",
  ],
  libraries: [
    "Redux",
    "Axios",
    "Socket.io",
    "jQuery",
    "Material UI",
    "WebSockets",
    "OpenAPI",
    "Swagger API",
  ],
  cloudDatabases: [
    "MySQL",
    "PostgreSQL",
    "MongoDB",
    "ElasticSearch",
    "Redis",
    "Firebase Firestore",
    "DynamoDB",
    "Apache HTTP Server",
    "Nginx",
  ],
  devOps: [
    "Docker",
    "Kubernetes",
    "AWS (EC2, S3, RDS, Lambda)",
    "Google Cloud Platform (GCP)",
    "Azure DevOps",
    "CI/CD Pipelines (Jenkins, GitHub Actions)",
    "Terraform",
    "Ansible",
    "Kafka",
    "RabbitMQ",
    "PM2",
    "Linux",
    "Nginx Reverse Proxy",
  ],
  aiMl: [
    "OpenAI API",
    "LLM Integration (OpenAI)",
    "TensorFlow",
    "PyTorch",
    "Scikit-Learn",
    "OCR",
    "LangChain",
    "Vector DBs",
  ],
  paymentGateways: [
    "Razorpay",
    "Stripe",
    "PayPal",
    "Authorize.net",
    "Cashfree",
  ],
  webDevTools: [
    "VSCode",
    "IntelliJ IDEA",
    "Eclipse",
    "Git",
    "GitHub",
    "GitLab",
    "Bitbucket",
    "Postman",
    "Composer",
  ],
};
const tabs = [
  ["languages", "Languages"],
  ["frameworks", "Frameworks"],
  ["libraries", "Libraries"],
  ["cloudDatabases", "DB & Servers"],
  ["devOps", "Cloud & DevOps"],
  ["aiMl", "AI / ML"],
  ["paymentGateways", "Payments"],
  ["webDevTools", "Tools"],
];
const contextualHighlights = {
  languages: [
    "Clean code practices, patterns & testing culture",
    "Type-safe APIs with TypeScript; performance-focused C/C++",
    "Python for automation, tooling, and AI/ML workflows",
  ],
  frameworks: [
    "SPA/MPA with React & Next.js (SSR / SSG)",
    "Laravel & Django for scalable back-office systems",
    "GraphQL/Apollo with modern ORMs (Prisma / TypeORM / Sequelize)",
  ],
  libraries: [
    "Real-time systems using Socket.io & WebSockets",
    "State management with Redux Toolkit & Zustand",
    "API documentation using OpenAPI / Swagger",
  ],
  cloudDatabases: [
    "Relational & NoSQL schema design, migrations & backups",
    "Search & caching using ElasticSearch and Redis",
    "Nginx & Apache server hardening and tuning",
  ],
  devOps: [
    "Containerized deployments with Docker & Kubernetes",
    "CI/CD automation and Infrastructure as Code",
    "Event-driven systems using Kafka & RabbitMQ",
  ],
  aiMl: [
    "LLM integrations using OpenAI & LangChain pipelines",
    "OCR and vector search for document intelligence",
    "Prompt engineering, evaluation & safety guardrails",
  ],
  paymentGateways: [
    "Secure checkout, webhooks & subscription flows",
    "Refunds, reconciliation, and reporting systems",
    "PCI-compliant tokenized payment architectures",
  ],
  webDevTools: [
    "GitFlow, PR reviews, and semantic versioning",
    "API testing with Postman; developer-first tooling",
    "Automation via scripts, CLIs, and internal tools",
  ],
};

  const activeLabel = useMemo(
    () => tabs.find(([k]) => k === activeTab)?.[1] || "",
    [activeTab]
  );

  const chip = (label) => (
    <span
      key={label}
      className="chip"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,0.18)",
        color: "#fff",
        background: "rgba(255,255,255,0.02)",
        fontSize: "0.9rem",
        whiteSpace: "nowrap",
      }}
      title={label}
    >
      <FontAwesomeIcon
        icon={iconMap[label] || faToolbox}
        style={{ opacity: 0.9 }}
      />
      {label}
    </span>
  );

  return (
    <section className="skills-section" id="skills">
      <style jsx>{`
        .skills-section {
          padding: 56px 0 64px;
          /* More transparent than before */
          background: linear-gradient(180deg, rgba(0,0,0,.72), rgba(0,0,0,.48));
          position: relative;
          overflow: hidden;
        }
        .skills-title {
          color: #fff;
          text-align: center;
          font-weight: 800;
          letter-spacing: .3px;
          margin-bottom: 18px;
          font-size: 1.9rem;
        }
        .tabs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
          margin-bottom: 26px;
        }
        .tab {
          color: #e6e6f0;
          border: 1px solid rgba(255,255,255,.15);
          background: rgba(255,255,255,.04);
          border-radius: 999px;
          padding: 8px 14px;
          cursor: pointer;
          font-size: .95rem;
          transition: all .18s ease;
        }
        .tab:hover { border-color: rgba(255,255,255,.28); transform: translateY(-1px); }
        .tab.active { color: #fff; border-color: rgba(124,58,237,.55); background: rgba(124,58,237,.15); }

        .grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .card {
          background: linear-gradient(180deg, rgba(23,23,42,.86), rgba(17,17,31,.84));
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 8px;
          padding: 18px;
          color: #fff;
          backdrop-filter: blur(5px);
          transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
        }
        .card:hover { transform: translateY(-2px); border-color: rgba(124,58,237,.35); box-shadow: 0 18px 36px rgba(124,58,237,.18); }
        .card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .card-title {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          letter-spacing: .2px;
        }
        .chip-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .list {
          margin: 6px 0 0;
          padding-left: 18px;
          color: #d6d8e5;
          line-height: 1.55;
        }
        .background-image-left { position: absolute; inset: 0; opacity: .12; z-index: -1; }

        @media (max-width: 960px) {
          .grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="container">
        <h2 className="skills-title">Technical Skills & Interests</h2>

        <div className="tabs">
          {tabs.map(([key, label]) => (
            <div
              key={key}
              className={`tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </div>
          ))}
        </div>

        <motion.div
          className="grid"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          {/* Skills chips */}
          <div className="card">
            <div className="card-head">
              <div className="card-title">
                <FontAwesomeIcon
                  icon={
                    activeTab === "languages"
                      ? faTerminal
                      : activeTab === "frameworks"
                      ? faCube
                      : activeTab === "libraries"
                      ? faToolbox
                      : activeTab === "cloudDatabases"
                      ? faServer
                      : activeTab === "devOps"
                      ? faCloud
                      : activeTab === "aiMl"
                      ? faRobot
                      : activeTab === "paymentGateways"
                      ? faMoneyCheckDollar
                      : faCogs
                  }
                />
                {activeLabel}
              </div>
            </div>
            <div className="chip-wrap">
              {skills[activeTab].map((s) => chip(s))}
            </div>
          </div>

          {/* Contextual Highlights */}
          <div className="card">
            <div className="card-head">
              <div className="card-title">
                <FontAwesomeIcon icon={faShieldAlt} />
                Highlights — {activeLabel}
              </div>
            </div>
            <ul className="list">
              {contextualHighlights[activeTab].map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>

      <img className="background-image-left" src={colorSharp} alt="background" />
    </section>
  );
};
